import React, { useEffect, useRef, useState } from 'react';
import { X, Search, Loader2, CheckCircle2, XCircle, FileText } from 'lucide-react';
import { API } from '../../Common/Api';

// Indian registration numbers look like RJ52GB1501 / DL8CAF5030. Anything else
// entered here (typically a 17-character VIN) is treated as a chassis number.
const VRN_PATTERN = /^[A-Z]{2}[0-9]{1,2}[A-Z]{0,3}[0-9]{4}$/;

const isVehicleNumber = (value) => VRN_PATTERN.test(value);

// One field accepts several entries — split on commas, semicolons, or whitespace.
export const parseEntries = (raw) =>
  (raw || '')
    .split(/[,;\s\n]+/)
    .map((v) => v.trim().toUpperCase())
    .filter(Boolean)
    .filter((v, i, arr) => arr.indexOf(v) === i);

const saveBlob = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const FitmentCertificateModal = ({ onClose }) => {
  const [input, setInput] = useState('');
  const [items, setItems] = useState([]); // { value, type, status, message }
  const [running, setRunning] = useState(false);
  const modalRef = useRef(null);

  // Close on outside click / Escape, but not mid-download.
  useEffect(() => {
    const onDocClick = (e) => {
      if (!running && modalRef.current && !modalRef.current.contains(e.target)) onClose();
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape' && !running) onClose();
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose, running]);

  const done = items.filter((i) => i.status === 'success' || i.status === 'failed').length;
  const succeeded = items.filter((i) => i.status === 'success').length;
  const failed = items.filter((i) => i.status === 'failed').length;
  const percent = items.length ? Math.round((done / items.length) * 100) : 0;

  const handleDownload = async () => {
    const entries = parseEntries(input);
    if (!entries.length || running) return;

    const queued = entries.map((value) => ({
      value,
      type: isVehicleNumber(value) ? 'vehicle' : 'chassis',
      status: 'pending',
      message: '',
    }));
    setItems(queued);
    setRunning(true);

    // Sequential so the progress bar advances predictably and the backend is not
    // hit with a burst of PDF generations at once.
    for (let i = 0; i < queued.length; i++) {
      const item = queued[i];
      setItems((prev) => prev.map((p, idx) => (idx === i ? { ...p, status: 'loading' } : p)));
      try {
        const blob = await API.DownloadFitmentPdf(
          item.type === 'vehicle'
            ? { vehicle_number: item.value }
            : { chassis_number: item.value }
        );
        saveBlob(blob, `fitment_certificate_${item.value}.pdf`);
        setItems((prev) =>
          prev.map((p, idx) => (idx === i ? { ...p, status: 'success', message: 'Downloaded' } : p))
        );
      } catch (err) {
        setItems((prev) =>
          prev.map((p, idx) =>
            idx === i ? { ...p, status: 'failed', message: err?.message || 'Download failed' } : p
          )
        );
      }
    }

    setRunning(false);
  };

  const StatusRow = ({ item }) => {
    const icon =
      item.status === 'success' ? <CheckCircle2 className="w-4 h-4 text-green-600" />
        : item.status === 'failed' ? <XCircle className="w-4 h-4 text-[#EE3B31]" />
          : item.status === 'loading' ? <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
            : <FileText className="w-4 h-4 text-gray-300" />;
    const label =
      item.status === 'success' ? 'Downloaded'
        : item.status === 'failed' ? (item.message || 'Failed')
          : item.status === 'loading' ? 'Generating...'
            : 'Queued';
    const labelClass =
      item.status === 'success' ? 'text-green-700'
        : item.status === 'failed' ? 'text-[#EE3B31]'
          : 'text-gray-500';

    return (
      <div className="flex items-start justify-between gap-3 px-3 py-2.5 border-b border-gray-50 last:border-0">
        <div className="flex items-center gap-2 min-w-0">
          <span className="shrink-0">{icon}</span>
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-800 break-all leading-tight">{item.value}</p>
            <p className="text-[11px] text-gray-400 uppercase tracking-wide">
              {item.type === 'vehicle' ? 'Vehicle No' : 'Chassis No'}
            </p>
          </div>
        </div>
        <span className={`text-xs text-right shrink-0 max-w-[45%] break-words ${labelClass}`}>{label}</span>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[999] flex justify-center items-center p-4">
      <div
        ref={modalRef}
        className="bg-white rounded-3xl shadow-2xl w-full max-w-md mx-auto overflow-hidden relative border border-gray-100"
      >
        {/* Header */}
        <div className="flex justify-between items-center px-6 pt-6 pb-4 border-b border-gray-50">
          <h3 className="text-xl font-bold text-gray-900">Download Fitment Certificate</h3>
          <button
            onClick={onClose}
            disabled={running}
            className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
            title="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          <div className="relative">
            <input
              type="text"
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                if (items.length > 0) setItems([]);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleDownload();
              }}
              disabled={running}
              placeholder="Vehicle or Chassis Number"
              className="w-full h-[44px] pl-3.5 pr-10 border border-gray-300 bg-white text-sm text-gray-800 placeholder-gray-400 rounded-lg uppercase placeholder:normal-case focus:outline-none focus:border-[#EE3B31] focus:ring-2 focus:ring-[#EE3B31]/20 transition-colors disabled:bg-gray-50"
            />
            {input && !running ? (
              <button
                type="button"
                onClick={() => {
                  setInput('');
                  setItems([]);
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer p-0.5 rounded-full hover:bg-gray-100"
                title="Clear input"
              >
                <X className="w-4 h-4" />
              </button>
            ) : (
              <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                <Search className="w-4 h-4" />
              </div>
            )}
          </div>
          <p className="mt-1.5 text-[11px] text-gray-400">
            For more than one, separate them with a comma or a space.
          </p>

          <button
            onClick={handleDownload}
            disabled={running || !parseEntries(input).length}
            className="mt-4 w-full h-[42px] flex items-center justify-center gap-2 rounded-lg bg-[#EE3B31] text-white text-sm font-semibold transition-all duration-200 hover:bg-[#d43229] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {running ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {running ? `Downloading ${done}/${items.length}...` : 'Download'}
          </button>

          {/* Progress */}
          {items.length > 0 && (
            <div className="mt-5">
              <div className="flex items-center justify-between text-xs text-gray-500 mb-1.5">
                <span>
                  {done} of {items.length} processed
                </span>
                <span>
                  {succeeded > 0 && <span className="text-green-700 font-medium">{succeeded} success</span>}
                  {succeeded > 0 && failed > 0 && <span className="text-gray-300"> · </span>}
                  {failed > 0 && <span className="text-[#EE3B31] font-medium">{failed} failed</span>}
                </span>
              </div>
              <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#EE3B31] rounded-full transition-all duration-300"
                  style={{ width: `${percent}%` }}
                />
              </div>

              <div className="mt-3 max-h-56 overflow-y-auto border border-gray-100 rounded-xl">
                {items.map((item, idx) => (
                  <StatusRow key={`${item.value}-${idx}`} item={item} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FitmentCertificateModal;
